import React, { useEffect, useMemo, useState } from "react";
import { RequireStaff } from "@/components/RequireStaff";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOnlineStaff } from "@/hooks/useOnlineStaff";
import { estimatePayroll } from "@/lib/estimatePayroll";
import { db } from "@/lib/firebase";
import {
  collection, query, where, getDocs, Timestamp,
} from "firebase/firestore";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Circle, Clock, DollarSign, Users, AlertTriangle, Loader2 } from "lucide-react";
import { PendingBookingsPanel } from "@/components/staff/PendingBookingsPanel";

const T = {
  en: {
    title: "Finance", subtitle: "Time tracking, pay estimates, and team presence.",
    myWeek: "My Week", hoursWorked: "Hours worked", regular: "Regular", overtime: "Overtime",
    estimate: "Estimated Weekly Pay", estimateNote: "Estimate only — not an official pay stub. Verify with an accountant or payroll provider before any real disbursement.",
    gross: "Gross", deductions: "Deductions", net: "Net Pay",
    federalTax: "Federal income tax (est.)", socialSecurity: "Social Security", medicare: "Medicare",
    noRate: "No hourly rate configured yet — contact an admin.",
    onlineNow: "Online Now", noOneOnline: "No staff currently online.",
    teamHours: "Team Hours This Week", name: "Name", hours: "Hours", rate: "Rate", estGross: "Est. Gross",
    away: "Away", online: "Online",
  },
  fr: {
    title: "Finance", subtitle: "Suivi des heures, estimation de paie et présence de l'équipe.",
    myWeek: "Ma semaine", hoursWorked: "Heures travaillées", regular: "Régulières", overtime: "Supplémentaires",
    estimate: "Estimation de paie hebdomadaire", estimateNote: "Estimation seulement — pas un talon de paie officiel. À valider avec un comptable ou un service de paie avant tout versement réel.",
    gross: "Brut", deductions: "Déductions", net: "Salaire net",
    federalTax: "Impôt fédéral (est.)", socialSecurity: "Sécurité sociale", medicare: "Medicare",
    noRate: "Aucun taux horaire configuré — contacte un admin.",
    onlineNow: "En ligne maintenant", noOneOnline: "Aucun membre du staff en ligne.",
    teamHours: "Heures de l'équipe cette semaine", name: "Nom", hours: "Heures", rate: "Taux", estGross: "Brut est.",
    away: "Absent", online: "En ligne",
  },
};

function currentWeekId(): string {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  const week = Math.ceil(((now.getTime() - jan1.getTime()) / 86_400_000 + jan1.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

function entryHours(clockIn: any, clockOut: any): number {
  const start = clockIn?.toDate ? clockIn.toDate() : null;
  const end = clockOut?.toDate ? clockOut.toDate() : new Date();
  if (!start) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / 3_600_000);
}

export default function StaffFinance() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const isAdmin = user?.roles?.includes("admin");

  const [myHours, setMyHours] = useState(0);
  const [myRate, setMyRate] = useState<number | null>(null);
  const [loadingMine, setLoadingMine] = useState(true);

  const [teamRows, setTeamRows] = useState<{ staffId: string; name: string; hours: number; rate: number | null }[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(isAdmin);

  const { presence, loading: presenceLoading, onlineCount } = useOnlineStaff();

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      setLoadingMine(true);
      try {
        const wk = currentWeekId();
        const q = query(
          collection(db, "staffTimeEntries"),
          where("staffId", "==", user.uid),
          where("weekId", "==", wk)
        );
        const snap = await getDocs(q);
        const total = snap.docs.reduce((sum, d) => sum + entryHours(d.data().clockIn, d.data().clockOut), 0);
        setMyHours(total);

        const rateSnap = await getDocs(query(collection(db, "staffPayRates"), where("__name__", "==", user.uid)));
        if (!rateSnap.empty) {
          setMyRate(rateSnap.docs[0].data().hourlyRate ?? null);
        }
      } catch (err) {
        console.error("[Finance] my hours load failed:", err);
      } finally {
        setLoadingMine(false);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoadingTeam(true);
      try {
        const wk = currentWeekId();
        const entriesSnap = await getDocs(query(collection(db, "staffTimeEntries"), where("weekId", "==", wk)));
        const byStaff: Record<string, number> = {};
        entriesSnap.docs.forEach((d) => {
          const data = d.data();
          byStaff[data.staffId] = (byStaff[data.staffId] || 0) + entryHours(data.clockIn, data.clockOut);
        });

        const ratesSnap = await getDocs(collection(db, "staffPayRates"));
        const rates: Record<string, number> = {};
        ratesSnap.docs.forEach((d) => { rates[d.id] = d.data().hourlyRate; });

        const usersSnap = await getDocs(collection(db, "users"));
        const names: Record<string, string> = {};
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          if (Object.prototype.hasOwnProperty.call(byStaff, d.id)) {
            names[d.id] = data.displayName || data.email || d.id;
          }
        });

        setTeamRows(
          Object.entries(byStaff).map(([staffId, hours]) => ({
            staffId, hours, name: names[staffId] || staffId, rate: rates[staffId] ?? null,
          })).sort((a, b) => b.hours - a.hours)
        );
      } catch (err) {
        console.error("[Finance] team hours load failed:", err);
      } finally {
        setLoadingTeam(false);
      }
    })();
  }, [isAdmin]);

  const myEstimate = useMemo(() => {
    if (myRate == null) return null;
    const regularHours = Math.min(myHours, 40);
    const overtimeHours = Math.max(0, myHours - 40);
    return estimatePayroll({ regularHours, overtimeHours, hourlyRate: myRate, bonus: 0 });
  }, [myHours, myRate]);

  return (
    <RequireStaff>
      <div className="font-['Geist',sans-serif] space-y-6">
        <header>
          <h1 className="text-xl font-semibold text-white">{t.title}</h1>
          <p className="text-sm text-gray-400 mt-1">{t.subtitle}</p>
        </header>

        <section className="rounded-2xl p-5 space-y-4" style={{ background: "#1b2a3a" }}>
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" /> {t.myWeek}
          </h2>
          {loadingMine ? (
            <Loader2 className="w-5 h-5 animate-spin text-white/30" />
          ) : (
            <>
              <div className="rounded-xl p-4 bg-white/5 flex items-center justify-between">
                <span className="text-sm text-gray-300">{t.hoursWorked}</span>
                <span className="text-lg font-semibold text-white">{myHours.toFixed(1)}h</span>
              </div>

              {myRate == null ? (
                <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {t.noRate}
                </div>
              ) : myEstimate && (
                <div className="rounded-xl p-4 bg-white/5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5 text-amber-400" /> {t.estimate}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <div className="flex justify-between"><span>{t.gross}</span><span className="text-gray-200">${myEstimate.grossPay.toFixed(2)}</span></div>
                    <div className="flex justify-between pl-3"><span>{t.federalTax}</span><span>-${myEstimate.deductions.federalIncomeTax.toFixed(2)}</span></div>
                    <div className="flex justify-between pl-3"><span>{t.socialSecurity}</span><span>-${myEstimate.deductions.socialSecurity.toFixed(2)}</span></div>
                    <div className="flex justify-between pl-3"><span>{t.medicare}</span><span>-${myEstimate.deductions.medicare.toFixed(2)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-white/10 font-semibold text-emerald-300">
                      <span>{t.net}</span><span>${myEstimate.netPay.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 italic pt-1">{t.estimateNote}</p>
                </div>
              )}
            </>
          )}
        </section>

        <section className="rounded-2xl p-5" style={{ background: "#1b2a3a" }}>
          <PendingBookingsPanel />
        </section>

        {isAdmin && (
          <>
            <section className="rounded-2xl p-5 space-y-3" style={{ background: "#1b2a3a" }}>
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-400" /> {t.onlineNow}
                <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-300">{onlineCount}</Badge>
              </h2>
              {presenceLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-white/30" />
              ) : presence.length === 0 ? (
                <p className="text-xs text-gray-500">{t.noOneOnline}</p>
              ) : (
                <ScrollArea className="max-h-52">
                  <div className="space-y-1.5">
                    {presence.map((p) => (
                      <div key={p.staffId} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Circle className={`w-2 h-2 ${p.state === "online" ? "fill-emerald-400 text-emerald-400" : "fill-gray-500 text-gray-500"}`} />
                          <span className="text-sm text-white">{p.displayName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.currentPage && <span className="text-[10px] text-gray-500 font-mono">{p.currentPage}</span>}
                          <Badge variant="outline" className={`text-[10px] ${p.state === "online" ? "border-emerald-500/40 text-emerald-300" : "border-gray-500/40 text-gray-400"}`}>
                            {p.state === "online" ? t.online : t.away}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </section>

            <section className="rounded-2xl p-5 space-y-3" style={{ background: "#1b2a3a" }}>
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" /> {t.teamHours}
              </h2>
              {loadingTeam ? (
                <Loader2 className="w-5 h-5 animate-spin text-white/30" />
              ) : (
                <ScrollArea className="max-h-96">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 text-xs uppercase border-b border-white/10">
                        <th className="py-2">{t.name}</th>
                        <th className="py-2">{t.hours}</th>
                        <th className="py-2">{t.rate}</th>
                        <th className="py-2">{t.estGross}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {teamRows.map((row) => (
                        <tr key={row.staffId}>
                          <td className="py-2 text-white">{row.name}</td>
                          <td className="py-2 text-gray-300">{row.hours.toFixed(1)}h</td>
                          <td className="py-2 text-gray-300">{row.rate != null ? `$${row.rate}/h` : "—"}</td>
                          <td className="py-2 text-gray-300">
                            {row.rate != null ? `$${(row.hours * row.rate).toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </section>
          </>
        )}
      </div>
    </RequireStaff>
  );
}